import { useEffect, useRef, type CSSProperties } from 'react';
import { Check, CircleAlert, Hand, History, Pause, Play, Plus, Radio, ScanEye, Users } from 'lucide-react';
import { Button, Card, IconButton, ScrollArea, Typography } from '../../ui';
import { useTranslation } from '../../../../lib/i18n';
import { useLocale } from '../../../../lib/locale-context';
import type { RecapChapter, RecapEvent } from '../../../../lib/home-map/world-recap';
import styles from './WorldRecapTimeline.module.css';

const CHAPTER_ICONS = { created: Plus, started: Play, review: ScanEye, needsYou: Hand, failed: CircleAlert, completed: Check, meeting: Users };

export function WorldRecapTimeline({ events, chapters, index, until, paused, speed, loading, error, partial, onPlay, onSpeed, onSeek, onLive, onRetry, onAction }: {
  events: RecapEvent[]; chapters: RecapChapter[]; index: number; until: number; paused: boolean; speed: number;
  loading: boolean; error: boolean; partial: boolean;
  onPlay: () => void; onSpeed: () => void; onSeek: (index: number) => void; onLive: () => void; onRetry: () => void;
  onAction: (name: string, action: () => void) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const current = events[index];
  const last = Math.max(0, events.length - 1);
  const progress = last ? index / last * 100 : 0;
  const activeChapter = [...chapters].reverse().find(chapter => chapter.index <= index);
  const chapterRow = useRef<HTMLElement>(null);
  useEffect(() => {
    const row = chapterRow.current;
    const active = row?.querySelector('[data-active="true"]');
    if (!row || !active) return;
    const viewport = row.getBoundingClientRect(), card = active.getBoundingClientRect();
    if (card.left < viewport.left || card.right > viewport.right) row.scrollBy({ left: card.left + card.width / 2 - viewport.left - viewport.width / 2 });
  }, [activeChapter?.index]);
  const time = (at: number) => new Date(at).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  const date = (at: number) => new Date(at).toLocaleDateString(locale, { month: 'short', day: 'numeric' });
  const seek = (value: number) => onSeek(Math.max(0, Math.min(last, value)));
  return <Card component="section" className={styles.root} aria-label={t('home.world.recap')}>
    <div className={styles.header}>
      <div className={styles.identity}>
        <History size={18} aria-hidden />
        <div><Typography variant="p2">{t('home.world.recap')}</Typography>
          <Typography variant="p3" color="secondary">{date(current?.at ?? until)}</Typography></div>
      </div>
      <div className={styles.playback}>
        <IconButton variant="contained" aria-label={t(paused ? 'home.map.play' : 'home.map.pause')} disabled={!events.length} onClick={() => onAction('recap-play', onPlay)}>
          {paused ? <Play size={18} aria-hidden /> : <Pause size={18} aria-hidden />}
        </IconButton>
        <Button size="small" variant="text" aria-label={t('home.world.speed', { speed })} onClick={() => onAction('recap-speed', onSpeed)}>{speed}×</Button>
      </div>
      <Button size="small" variant="outlined" startIcon={<Radio size={14} aria-hidden />} onClick={() => onAction('recap-live', onLive)}>{t('home.map.live')}</Button>
    </div>
    {current ? <>
      <div className={styles.position}>
        <span className={styles.timestamp}>{time(current.at)}</span>
        <span className={styles.currentTask}>{current.source === 'snapshot' ? t('home.world.recapHint') : current.source === 'meeting' ? current.title || t('home.map.brief') : current.title}</span>
        <span className={styles.count}>{index + 1} / {events.length}</span>
      </div>
      <div className={styles.track} style={{ '--recap-progress': `${progress}%` } as CSSProperties}>
        <div className={styles.rail} aria-hidden><div className={styles.fill} />
          {chapters.map(chapter => <i key={`${chapter.index}:${chapter.kind}`} data-kind={chapter.kind} style={{ insetInlineStart: `${last ? chapter.index / last * 100 : 0}%` }} />)}
        </div>
        <input type="range" min={0} max={last} value={index} disabled={!last} aria-label={t('home.map.timeline')} aria-valuetext={new Date(current.at).toLocaleString(locale)}
          onChange={event => seek(Number(event.target.value))} onPointerUp={() => onAction('recap-scrub', () => {})} onKeyUp={() => onAction('recap-scrub', () => {})} />
      </div>
      <div className={styles.scale}><span>{date(events[0].at)} · {time(events[0].at)}</span><span>{date(until)} · {time(until)} · {t('home.map.live')}</span></div>
      {chapters.length > 0 && <ScrollArea ref={chapterRow} axis="horizontal" className={styles.chapters} role="group" aria-label={t('home.world.chapters')}>
        {chapters.map(chapter => {
          const Icon = CHAPTER_ICONS[chapter.kind];
          const active = chapter === activeChapter;
          return <Button key={`${chapter.index}:${chapter.kind}`} variant="text" className={styles.chapter} aria-pressed={active} data-active={active} data-kind={chapter.kind}
            onClick={() => onAction('recap-chapter', () => seek(chapter.index))}>
            <span className={styles.chapterIcon}><Icon size={16} aria-hidden /></span>
            <span className={styles.chapterCopy}><span className={styles.chapterMeta}>{t(chapter.kind === 'meeting' ? 'home.map.brief' : `home.world.chapter.${chapter.kind}`)}<time>{time(chapter.at)}</time></span>
              <span className={styles.chapterTitle}>{chapter.title || t('home.map.brief')}</span></span>
          </Button>;
        })}
      </ScrollArea>}
    </> : <Typography variant="p3" color="secondary">{t(error ? 'agents.activity.error' : loading ? 'common.loading' : 'home.map.quiet')}</Typography>}
    {partial && <Typography variant="p3" color="secondary">{t('home.world.recapPartial')}</Typography>}
    {error && <Button size="small" variant="outlined" onClick={() => onAction('recap-retry', onRetry)}>{t('common.retry')}</Button>}
  </Card>;
}
